import React, { useMemo } from "react";
import parse from "html-react-parser";
import styled, { keyframes } from "styled-components";
import { T } from "@transifex/react";
import {
  useAiContent,
  AiContentResponse,
  AiContentJsonShape,
} from "hooks/use-ai-content";
import { isYouTubeUrl, extractYouTubeVideoId } from "helpers/youtube";
import { YouTubeEmbed } from "./YouTubeEmbed";
import {
  ContentItemWrapper,
  ContentItemHtml,
  ContentItemVideo,
  ContentItemVideoTitle,
} from "./styles";

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const LoadingContainer = styled.div`
  padding: 16px 0;
`;

const LoadingBar = styled.div<{ $width?: string; $height?: string }>`
  background: linear-gradient(90deg, #f0f0f0 0px, #e0e0e0 40px, #f0f0f0 80px);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: 4px;
  width: ${(props) => props.$width || "100%"};
  height: ${(props) => props.$height || "16px"};
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LoadingText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  margin-top: 12px;
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e0e0e0;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  padding: 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
`;

const ErrorText = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
`;

const RetryButton = styled.button`
  background: #1976d2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #1565c0;
  }
`;

interface AiContentItemProps {
  /** HTML content or AI prompt */
  content?: string;
  /** Video URL */
  videoUrl?: string;
  /** Video title */
  videoTitle?: string;
  /** If true, content is an AI prompt that needs to be sent to the specialist API */
  isAiPrompt?: boolean;
  /** Index for data-testid */
  index: number;
}

/**
 * Renders a content item with support for AI-generated content.
 *
 * When isAiPrompt is true, the content is treated as an AI prompt
 * and sent to the specialist API to generate HTML content.
 * A loading skeleton is displayed while the content is being generated.
 */
export function AiContentItem({
  content,
  videoUrl: rawVideoUrl,
  videoTitle,
  isAiPrompt = false,
  index,
}: AiContentItemProps) {
  const videoUrl = rawVideoUrl?.trim() || undefined;
  const {
    content: generatedContent,
    isLoading,
    error,
    retry,
  } = useAiContent({
    prompt: content || "",
    isAiPrompt,
  });

  const hasContent = isAiPrompt ? generatedContent != null : !!content;

  const renderedContent = useMemo(() => {
    if (!hasContent) return null;

    // Non-AI path: content is static HTML string, use parse()
    if (!isAiPrompt) {
      return (
        <ContentItemHtml data-testid={`InterestCard__ContentItemHtml-${index}`}>
          {parse(content || "")}
        </ContentItemHtml>
      );
    }

    const response = generatedContent as AiContentResponse | null;
    if (!response) return null;

    // AI JSON path: render structured data as React elements directly
    if (
      response.format === "json" &&
      typeof response.content === "object" &&
      response.content !== null
    ) {
      const obj = response.content as AiContentJsonShape;
      return (
        <ContentItemHtml data-testid={`InterestCard__ContentItemHtml-${index}`}>
          {obj.heading && <h3>{obj.heading}</h3>}
          {obj.body && <p>{obj.body}</p>}
          {Array.isArray(obj.key_points) && obj.key_points.length > 0 && (
            <ul>
              {obj.key_points
                .filter((p): p is string => typeof p === "string")
                .map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
            </ul>
          )}
          {obj.cta_url && obj.cta_url.trim() !== "" && (
            <a href={obj.cta_url.trim()}>
              {obj.cta_text && obj.cta_text.trim() !== ""
                ? obj.cta_text
                : obj.cta_url}
            </a>
          )}
        </ContentItemHtml>
      );
    }

    // AI HTML path: content is an HTML string, use parse()
    if (typeof response.content === "string") {
      return (
        <ContentItemHtml data-testid={`InterestCard__ContentItemHtml-${index}`}>
          {parse(response.content)}
        </ContentItemHtml>
      );
    }

    return null;
  }, [hasContent, isAiPrompt, content, generatedContent, index]);

  if (isAiPrompt && isLoading) {
    return (
      <ContentItemWrapper data-testid={`InterestCard__ContentItem-${index}`}>
        <LoadingContainer
          data-testid={`InterestCard__ContentItemLoading-${index}`}
        >
          <LoadingBar $width="90%" />
          <LoadingBar $width="100%" />
          <LoadingBar $width="75%" />
          <LoadingBar $width="85%" />
          <LoadingText>
            <Spinner />
            <T _str="Generating personalized content..." />
          </LoadingText>
        </LoadingContainer>
      </ContentItemWrapper>
    );
  }

  if (isAiPrompt && error) {
    return (
      <ContentItemWrapper data-testid={`InterestCard__ContentItem-${index}`}>
        <ErrorContainer data-testid={`InterestCard__ContentItemError-${index}`}>
          <ErrorText>
            <T _str="Unable to load personalized content." />
          </ErrorText>
          <RetryButton onClick={retry}>
            <T _str="Try again" />
          </RetryButton>
        </ErrorContainer>
      </ContentItemWrapper>
    );
  }

  if (!hasContent && !videoUrl) {
    return null;
  }

  return (
    <ContentItemWrapper data-testid={`InterestCard__ContentItem-${index}`}>
      {renderedContent}
      {videoUrl && (
        <ContentItemVideo
          data-testid={`InterestCard__ContentItemVideo-${index}`}
        >
          {isYouTubeUrl(videoUrl) ? (
            <YouTubeEmbed
              videoId={extractYouTubeVideoId(videoUrl)!}
              title={videoTitle}
            />
          ) : (
            <video
              src={videoUrl}
              controls
              preload="metadata"
              aria-label={videoTitle || "Video"}
            />
          )}
          {videoTitle && (
            <ContentItemVideoTitle>{videoTitle}</ContentItemVideoTitle>
          )}
        </ContentItemVideo>
      )}
    </ContentItemWrapper>
  );
}
